import React, { useState } from 'react';
import useSpring from 'react-use/lib/useSpring';
import { css } from '@emotion/core';
import Container from '../../components/Container';
import { FormattedHTMLMessage, useIntl } from 'gatsby-plugin-intl';
import { mainColor } from '../../constants/colors';
import NumberSlider from './NumberSlider';
import RiskSlider from './RiskSlider';
import CompositionCirle from './CompositionCircle';
import CompositionPercentage from './CompositionPercentage';
import { Desktop, Tablet } from '../../constants/responsive';
import { useMount } from 'react-use';

const riskData = [
  {
    id: 'conservative',
    normal: {
      annual: 0.0782,
      stocks: 0.362,
      bonds: 0.4,
      alternatives: 0.038,
      cash: 0.2,
    },
    max: {
      annual: 0.0374,
      stocks: 0.1554,
      bonds: 0.8363,
      alternatives: 0.0084,
      cash: 0,
    },
  },
  {
    id: 'moderate',
    normal: {
      annual: 0.0869,
      stocks: 0.462,
      bonds: 0.333,
      alternatives: 0.038,
      cash: 0.167,
    },
    max: {
      annual: 0.0586,
      stocks: 0.337,
      bonds: 0.6458,
      alternatives: 0.0172,
      cash: 0,
    },
  },
  {
    id: 'balanced',
    normal: {
      annual: 0.0944,
      stocks: 0.562,
      bonds: 0.267,
      alternatives: 0.038,
      cash: 0.133,
    },
    max: {
      annual: 0.0766,
      stocks: 0.5529,
      bonds: 0.4221,
      alternatives: 0.0253,
      cash: 0,
    },
  },
  {
    id: 'growth',
    normal: {
      annual: 0.1074,
      stocks: 0.662,
      bonds: 0.3,
      alternatives: 0.038,
      cash: 0,
    },
    max: {
      annual: 0.0848,
      stocks: 0.6582,
      bonds: 0.3126,
      alternatives: 0.0292,
      cash: 0,
    },
  },
  {
    id: 'aggressive',
    normal: {
      annual: 0.115,
      stocks: 0.762,
      bonds: 0.2,
      alternatives: 0.038,
      cash: 0,
    },
    max: {
      annual: 0.0923,
      stocks: 0.7612,
      bonds: 0.2059,
      alternatives: 0.0329,
      cash: 0,
    },
  },
];

const IndividualsPortfolio = () => {
  const intl = useIntl();
  const [selectedType, setSelectedType] = useState('max');
  const [selectedRisk, setSelectedRisk] = useState(2);
  const [selectedAmount, setSelectedAmount] = useState(200000);
  const [selectedYears, setSelectedYears] = useState(8);
  const earningsAmount = useSpring(
    selectedAmount *
      Math.pow(1 + riskData[selectedRisk][selectedType].annual, selectedYears) -
      selectedAmount,
    1000,
    100
  );

  const multiplier = selectedType === 'max' ? 0.1 : 1;
  const [isClient, setIsClient] = useState(false);
  useMount(() => {
    setIsClient(true);
  });
  return (
    <div
      css={css`
        margin-top: 24px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        ${Desktop} {
          margin-top: 100px;
        }
      `}
    >
      <h2>
        <FormattedHTMLMessage id='individuals.portfolio.title' />
      </h2>
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        {['max', 'normal'].map((type) => {
          return (
            <div
              key={`${type}-${isClient}`}
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                color: ${selectedType === type ? 'white' : mainColor};
                border: 2px solid ${mainColor};
                background: ${selectedType === type
                  ? mainColor
                  : 'transparent'};
                transition: all 0.2s ease;
                padding: 0px 20px;
                font-weight: bold;
                margin: 0px 10px;
                cursor: pointer;
                font-size: 12px;
                border-radius: 45px;
                height: 38px;
                ${Tablet} {
                  font-size: 16px;
                  border-radius: 45px;
                  height: 45px;
                }
                ${Desktop} {
                  font-size: 20px;
                  border-radius: 50px;
                  height: 50px;
                }
              `}
              onClick={() => {
                setSelectedType(type);
              }}
            >
              {{ max: 'SmartGlobal Max', normal: 'SmartGlobal' }[type]}
            </div>
          );
        })}
      </div>
      <RiskSlider
        riskData={riskData}
        value={selectedRisk}
        onChange={(val) => {
          setSelectedRisk(val);
        }}
      />
      <Container
        css={css`
          margin-top: 30px;
          width: 100%;
          display: flex;
          justify-content: center;
          ${Tablet} {
            margin-top: 50px;
          }
          ${Desktop} {
            margin-top: 100px;
          }
        `}
      >
        <div
          css={css`
            width: 100%;
            max-width: 910px;
            border-radius: 12px;
            box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
            background: white;
            display: flex;
            height: auto;
            flex-direction: column;
            align-items: center;
            padding: 10px;
            ${Tablet} {
              align-items: flex-start;
              padding: 20px;
              height: 360px;
              flex-direction: row;
            }
            ${Desktop} {
              border-radius: 30px;
              height: 470px;
            }
          `}
        >
          <div
            css={css`
              flex: 0 0 280px;
              width: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              ${Tablet} {
                flex: 1;
              }
            `}
          >
            <h5
              css={css`
                margin-top: 10px;
                line-height: 1;
                margin-bottom: 10px;
                ${Tablet} {
                  margin-top: 25px;
                }
                ${Desktop} {
                  margin-bottom: 24px;
                }
              `}
            >
              <b>
                <FormattedHTMLMessage id='individuals.portfolio.text-1' />
                {selectedYears}
                {selectedYears === 1 ? (
                  <FormattedHTMLMessage id='individuals.portfolio.text-2a' />
                ) : (
                  <FormattedHTMLMessage id='individuals.portfolio.text-2b' />
                )}
              </b>
            </h5>
            <div
              css={css`
                font-size: 25px;
                font-weight: bold;
                color: ${mainColor};
                line-height: 1;
                ${Desktop} {
                  font-size: 50px;
                }
              `}
            >
              {`${{ max: 'USD', normal: 'HKD' }[selectedType]} ${Number(
                parseInt(earningsAmount * multiplier)
              ).toLocaleString()}`}
            </div>
            <NumberSlider
              min={10000}
              max={1000000}
              step={10000}
              value={selectedAmount}
              onChange={(val) => {
                setSelectedAmount(val);
              }}
              displayFn={(val) => {
                return `${Number(val * multiplier).toLocaleString()}`;
              }}
              label={intl.formatMessage({
                id: `individuals.portfolio.amount${
                  { max: '-us', normal: '' }[selectedType]
                }`,
              })}
            />
            <NumberSlider
              min={1}
              max={20}
              step={1}
              value={selectedYears}
              onChange={(val) => {
                setSelectedYears(val);
              }}
              displayFn={(val) => {
                return val;
              }}
              label={
                <FormattedHTMLMessage id={`individuals.portfolio.years`} />
              }
            />
          </div>
          <div
            css={css`
              flex: 1 0 170px;
              width: 100%;
              max-width: 300px;
              border-radius: 12px;
              background-color: #f6f6f6;
              padding: 20px;
              display: flex;
              flex-direction: row;
              align-items: flex-start;
              justify-content: center;
              margin-top: 30px;
              ${Tablet} {
                max-width: none;
                padding: 20px;
                align-items: center;
                justify-content: flex-start;
                padding-bottom: 0px;
                flex-direction: column;
                margin-top: 0px;
                border-radius: 21px;
                margin-left: 40px;
                flex: 0 0 250px;
                height: 100%;
              }
            `}
          >
            <h5
              css={css`
                display: none;
                ${Tablet} {
                  display: inline-block;
                }
              `}
            >
              <b>
                <FormattedHTMLMessage id='individuals.portfolio.composition' />
              </b>
            </h5>
            <div
              css={css`
                position: relative;
                width: 110px;
                height: 110px;
                margin-right: 20px;
                ${Tablet} {
                  margin-right: 0px;
                  margin-bottom: 3px;
                  width: 100%;
                }
                ${Desktop} {
                  height: 220px;
                }
              `}
            >
              <CompositionCirle
                percent={
                  riskData[selectedRisk][selectedType].stocks +
                  riskData[selectedRisk][selectedType].bonds +
                  riskData[selectedRisk][selectedType].cash +
                  riskData[selectedRisk][selectedType].alternatives
                }
                color={'#cccccc'}
              />
              <CompositionCirle
                percent={
                  riskData[selectedRisk][selectedType].stocks +
                  riskData[selectedRisk][selectedType].bonds +
                  riskData[selectedRisk][selectedType].cash
                }
                color={'#fdb900'}
              />
              <CompositionCirle
                percent={
                  riskData[selectedRisk][selectedType].stocks +
                  riskData[selectedRisk][selectedType].bonds
                }
                color={'#00abc0'}
              />
              <CompositionCirle
                percent={riskData[selectedRisk][selectedType].stocks}
                color={mainColor}
              />
            </div>
            <div>
              <h5
                css={css`
                  display: block;
                  margin-bottom: 0px;
                  ${Tablet} {
                    display: none;
                  }
                `}
              >
                <b>
                  <FormattedHTMLMessage id='individuals.portfolio.composition' />
                </b>
              </h5>
              <CompositionPercentage
                label={
                  <FormattedHTMLMessage id='individuals.portfolio.stocks' />
                }
                value={riskData[selectedRisk][selectedType].stocks}
                color={mainColor}
              />
              <CompositionPercentage
                label={
                  <FormattedHTMLMessage id='individuals.portfolio.bonds' />
                }
                value={riskData[selectedRisk][selectedType].bonds}
                color={'#00abc0'}
              />
              {riskData[selectedRisk][selectedType].cash ? (
                <CompositionPercentage
                  label={
                    <FormattedHTMLMessage id='individuals.portfolio.cash' />
                  }
                  value={riskData[selectedRisk][selectedType].cash}
                  color={'#fdb900'}
                />
              ) : null}
              <CompositionPercentage
                label={
                  <FormattedHTMLMessage id='individuals.portfolio.alternatives' />
                }
                value={riskData[selectedRisk][selectedType].alternatives}
                color={'#cccccc'}
              />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default IndividualsPortfolio;
